import { useEffect, useState } from "react";
import { Link, Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { LogOut, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { LanguageSelector } from "@/components/LanguageSelector";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/saas/AuthContext";
import { useI18n } from "@/i18n";

export default function CustomerLayout() {
  const { org, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const NAV = [
    { to: "/", label: t("nav.home"), testid: "nav-homepage", plain: true },
    { to: "/dashboard", label: t("app.nav.dashboard"), testid: "nav-dashboard", end: true },
    { to: "/account", label: t("app.nav.account"), testid: "nav-account", end: true },
    { to: "/account/billing", label: t("app.nav.plans"), testid: "nav-plans-credits" },
  ];

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const doLogout = async () => { await logout(); nav("/login", { replace: true }); };

  const linkCls = ({ isActive }) =>
    `whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${isActive ? "bg-re-red/10 text-re-red" : "text-re-charcoal hover:bg-re-soft"}`;

  return (
    <div className="min-h-screen bg-re-soft">
      <Toaster position="top-center" richColors />
      <header className="sticky top-0 z-40 border-b border-re-line bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3.5 sm:px-6">
          <div className="flex min-w-0 items-center gap-6">
            <Link to="/dashboard" className="shrink-0" data-testid="layout-logo"><Logo variant="light" emblemY={7} /></Link>
            <nav className="hidden items-center gap-1 lg:flex">
              {NAV.map((n) => (
                n.plain
                  ? <Link key={n.testid} to={n.to} data-testid={n.testid} className="whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium text-re-charcoal transition-colors hover:bg-re-soft">{n.label}</Link>
                  : <NavLink key={n.testid} to={n.to} end={n.end} data-testid={n.testid} className={linkCls}>{n.label}</NavLink>
              ))}
            </nav>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <LanguageSelector />
            {/* Organisation name — secondary information only, never the main brand */}
            <span className="hidden max-w-[180px] truncate text-sm text-re-muted lg:inline" data-testid="header-org" title={org?.name}>{org?.name}</span>
            <Button variant="outline" size="sm" onClick={doLogout} data-testid="nav-logout" className="hidden rounded-full px-3 lg:inline-flex"><LogOut className="mr-1.5 h-4 w-4" />{t("app.nav.logout")}</Button>
            <Button variant="ghost" size="sm" onClick={() => setMenuOpen((v) => !v)} data-testid="mobile-menu-toggle" className="px-2 lg:hidden" aria-label="Menu">
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="border-t border-re-line bg-white px-4 py-3 lg:hidden" data-testid="mobile-nav">
            {org?.name && <p className="truncate px-1 pb-2 text-sm font-medium text-re-muted" data-testid="mobile-org">{org.name}</p>}
            <div className="flex flex-col gap-1">
              {NAV.map((n) => (
                n.plain
                  ? <Link key={n.testid} to={n.to} data-testid={`m${n.testid}`} className="rounded-full px-3 py-1.5 text-sm font-medium text-re-charcoal hover:bg-re-soft">{n.label}</Link>
                  : <NavLink key={n.testid} to={n.to} end={n.end} data-testid={`m${n.testid}`} className={linkCls}>{n.label}</NavLink>
              ))}
              <button onClick={doLogout} data-testid="mnav-logout" className="mt-1 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-left text-sm font-medium text-re-charcoal hover:bg-re-soft"><LogOut className="h-4 w-4" /> {t("app.nav.logout")}</button>
              <div className="mt-2 px-1"><LanguageSelector /></div>
            </div>
          </div>
        )}
      </header>
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6"><Outlet /></main>
    </div>
  );
}
